import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Package,
  Download,
  RefreshCw,
  AlertTriangle,
  Calendar,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/App";

const iconMap = {
  Package,
  RefreshCw,
  AlertTriangle,
  Calendar,
};

export default function Toolkits() {
  const [toolkits, setToolkits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchToolkits = async () => {
      try {
        const res = await api.get("/toolkits");
        setToolkits(res.data);
      } catch (error) {
        // Error fetching toolkits
      } finally {
        setLoading(false);
      }
    };
    fetchToolkits();
  }, []);

  const trackDownload = async (toolkitId, toolkitTitle) => {
    try {
      await api.post("/analytics/event", {
        event_type: "toolkit_download",
        resource_id: toolkitId,
        resource_type: "toolkit",
        metadata: {},
      });
      toast.success(`Downloading: ${toolkitTitle}`);
    } catch (error) {
      // Error tracking download - silent fail
    }
  };

  return (
    <div data-testid="toolkits-page">
      {/* Header */}
      <section className="bg-slate-900 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="label-uppercase text-orange-400 mb-4">Resources</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="toolkits-title">
              Toolkits
            </h1>
            <p className="text-lg text-slate-300">
              Bundled resources organized by topic. Each toolkit contains templates, 
              checklists, and guides related to specific PPS workflows. Download 
              individual files or grab the complete bundle.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Toolkits Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="toolkits-grid">
              {toolkits.map((toolkit, index) => {
                const Icon = iconMap[toolkit.icon] || Package;
                return (
                  <motion.div
                    key={toolkit.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div
                      className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col"
                      style={{ borderTopColor: toolkit.color, borderTopWidth: "4px" }}
                    >
                      <div className="p-6 flex-1">
                        <div 
                          className="w-12 h-12 rounded-sm flex items-center justify-center mb-4"
                          style={{ backgroundColor: `${toolkit.color}15` }}
                        >
                          <Icon 
                            className="w-6 h-6" 
                            style={{ color: toolkit.color }}
                            strokeWidth={1.5}
                          />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                          {toolkit.title}
                        </h3>
                        <p className="text-slate-600 mb-4">
                          {toolkit.summary}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="p-6 bg-slate-50 border-t border-slate-200 flex gap-3">
                        <Link to={`/toolkits/${toolkit.slug}`} className="flex-1">
                          <Button 
                            variant="outline" 
                            className="w-full"
                            data-testid={`view-toolkit-${toolkit.slug}`}
                          >
                            View Details
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                        {toolkit.zip_url && (
                          <Button
                            onClick={() => {
                              trackDownload(toolkit.id, toolkit.title);
                              window.open(toolkit.zip_url, "_blank");
                            }}
                            className="btn-primary"
                            data-testid={`download-toolkit-${toolkit.slug}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Coming Soon */}
          {!loading && toolkits.length === 0 && (
            <div className="text-center py-12 bg-slate-50 rounded-sm">
              <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                Toolkits Coming Soon
              </h2>
              <p className="text-slate-600">
                We're currently building out our toolkit library. Check back soon!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold mb-4">
            Need Custom Templates?
          </h2>
          <p className="text-emerald-100 mb-6">
            Looking for templates tailored to your district's specific needs? 
            Let's discuss how to customize these resources for your context.
          </p>
          <Link to="/about#contact">
            <Button className="h-11 px-6 bg-white text-emerald-600 hover:bg-emerald-50 font-medium" data-testid="contact-btn">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
